import { useGetMarketAccountDetailsQuery } from '@src/providers/AppStateProvider/streamingDataSlice';
import type { OTCDealData } from '@src/types/OTC';
import { isFixedRateLoanDeal, isFloatingRateLoanDeal } from '@src/types/OTC/otcGuards';
import { formatHolidayCalendars, formatSettlementCycle } from '@src/utils/OTC/otcUtils';
import { EMPTY_OBJECT, formattedDate } from '@talos/kyoko';
import { formatInTimeZone } from 'date-fns-tz';
import { useMemo } from 'react';
import { InspectorText } from '../InspectorText';
import type { DisplayOutputItem } from './types';

/**
 * Format a date value to date-only format with day of week (e.g. "Fri 2025-03-07")
 */
const formatDateOnlyWithDayOfWeek = (value: string | number | null | undefined, timezone: string = 'UTC'): string => {
  if (!value || value === '0') {
    return '';
  }
  try {
    const date = typeof value === 'string' ? new Date(value) : new Date(value);
    if (isNaN(date.getTime())) {
      return '';
    }
    return formatInTimeZone(date, timezone, 'EEE yyyy-MM-dd');
  } catch {
    return '';
  }
};

/**
 * Format date-time in the given timezone (matches ManualDealDateControls formatDateTime)
 */
const formatDateTimeInTimezone = (value: string | number | null | undefined, timezone: string = 'UTC'): string => {
  if (!value || value === '0') {
    return '';
  }
  try {
    const date = typeof value === 'string' ? new Date(value) : new Date(value);
    if (isNaN(date.getTime())) {
      return formattedDate(value, '{yyyy}-{MM}-{dd} {HH}:{mm}');
    }
    return formatInTimeZone(date, timezone, 'EEE yyyy-MM-dd HH:mm zzz');
  } catch {
    return formattedDate(value, '{yyyy}-{MM}-{dd} {HH}:{mm}');
  }
};

export const useTimingInformation = (instrumentData: OTCDealData): DisplayOutputItem => {
  const { data: marketAccountDetailsData } = useGetMarketAccountDetailsQuery(EMPTY_OBJECT);

  const timezone = useMemo(() => {
    const marketAccount = instrumentData.MarketAccount;
    if (!marketAccount || !marketAccountDetailsData) {
      return 'UTC';
    }
    const accountId = String(marketAccount);
    return marketAccountDetailsData.get(accountId)?.SettleCutoffTimezone ?? 'UTC';
  }, [instrumentData.MarketAccount, marketAccountDetailsData]);

  const tradeTime = instrumentData.TradeTime ?? instrumentData.DealInfo?.TradeTime;
  const effectiveTime = instrumentData.EffectiveTime ?? instrumentData.DealInfo?.EffectiveTime;
  const expireTime = instrumentData.ExpireTime ?? instrumentData.DealInfo?.ExpiryTime;
  const settleTime = instrumentData.SettleTime ?? instrumentData.DealInfo?.SettleTime;

  const settleCycle = instrumentData.DealInfo?.SettleCycle;
  const holidayCalendars = instrumentData.DealInfo?.HolidayCalendars;

  const expiryDateLabel =
    isFixedRateLoanDeal(instrumentData) || isFloatingRateLoanDeal(instrumentData) ? 'Maturity Date' : 'Expiration Date';

  return {
    sectionTitle: 'Contract Dates',
    fields: [
      {
        fieldName: 'Trade Date',
        fieldValue: <InspectorText>{formatDateOnlyWithDayOfWeek(tradeTime, timezone)}</InspectorText>,
      },
      {
        fieldName: 'Effective Date',
        fieldValue: <InspectorText>{formatDateOnlyWithDayOfWeek(effectiveTime, timezone)}</InspectorText>,
      },
      {
        fieldName: expiryDateLabel,
        fieldValue: <InspectorText>{formatDateTimeInTimezone(expireTime, timezone)}</InspectorText>,
      },
      {
        fieldName: 'Settlement Date',
        fieldValue: <InspectorText>{formatDateTimeInTimezone(settleTime, timezone)}</InspectorText>,
      },
      {
        fieldName: 'Settlement Cycle',
        fieldValue: <InspectorText>{formatSettlementCycle(settleCycle)}</InspectorText>,
      },
      {
        fieldName: 'Holiday Calendar',
        fieldValue: <InspectorText>{formatHolidayCalendars(holidayCalendars)}</InspectorText>,
      },
    ],
  };
};
